<template>
  <div id="inventoryEdit">
    <div class="container">

      <div class="page-title">
        <span class="name">{{ $t('inventory.title') }}</span>
        <button class="save btn btn-primary" @click="save"
          v-if="$store.getters.loggedUserHasModifyPermission">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('inventory.select_inventory')" v-if="branchInventories.length > 0 && isCompanyContext">
          <template v-if="branchInventories.length > 0 && isCompanyContext">
            <TipBox danger no-border>
              <span v-html="$t('tips.inventories.select_inventory')"></span>
            </TipBox>
            <div class="group">
              <input type="radio" id="chk_branchInventory" :value="true" v-model="selectedBranchInventory">
              <label for="chk_branchInventory">{{ $t('inventory.select_branch_inventory') }}</label>
              <input type="radio" id="chk_companyInventory" :value="false" v-model="selectedBranchInventory">
              <label for="chk_companyInventory">{{ $t('inventory.select_company_inventory') }}</label>
            </div>
            <div class="group">
              <label>{{ $t('inventory.title') }}</label>
              <select v-model="company.inventory" v-if="branchInventories.length > 0">
                <option :value="null" disabled hidden>{{ $t('company.third_party_delivery.choose') }}...</option>
                <option :value="company.originalInventory" v-if="!selectedBranchInventory && company.originalInventory">
                  {{ $t('company.custom_inventory') }}</option>
                <option :value="inventory.id" v-for="inventory of branchInventories" :key="`branch_inv_${inventory.id}`"
                  v-if="selectedBranchInventory">
                  {{ $to(inventory.name) }}
                </option>
                <option :value="inventory.id" v-for="inventory of company.archivedInventories"
                  :key="`archive_inv_${inventory.id}`" v-if="!selectedBranchInventory">
                  {{ $to(inventory.name) }}
                </option>
              </select>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('inventory.settings.title')" v-if="canEditInventory">
          <template v-if="canEditInventory">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('currency') }}</label>
                  <select v-model="inventory.currency">
                    <option :value="c.id" v-for="c of supportedCurrencies">{{ c.id }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('inventory.taxesIncluded') }}</label>
                  <select v-model="inventory.taxesIncluded">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('inventory.showItemsWhenZero') }}</label>
                  <select v-model="inventory.showItemsWhenZero">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('inventory.orderRequirementsActivated') }}</label>
                  <select v-model="inventory.orderRequirementsActivated">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('inventory.branding_activated') }}</label>
                  <select v-model="inventory.brandingActivated">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group" v-if="inventory.brandingActivated">
                  <label>{{ $t('inventory.allow_cross_brand_ordering') }}</label>
                  <select v-model="inventory.allowCrossBrandOrdering">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('languages') }}</label>
                  <br>
                  <div class="group offset" v-for="c of supportedLanguages" :key="`cur_${c.id}`">
                    <input :id="`chk_ln_${c.id}`" type="checkbox" :value="c.id" v-model="inventory.languages">
                    <label :for="`chk_ln_${c.id}`">{{ $t(c.transKey) }}</label>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('inventory.taxProfiles')" v-if="canEditInventory">
          <template v-if="canEditInventory">
            <TipBox info no-border :title="$t('tips.tax_profile.title')">
              <p>{{ $t('tips.tax_profile.description') }}</p>
              <ol>
                <li v-html="$t('tips.tax_profile.li_1')"></li>
                <li v-html="$t('tips.tax_profile.li_2')"></li>
                <li v-html="$t('tips.tax_profile.li_3')"></li>
                <li v-html="$t('tips.tax_profile.li_4')"></li>
                <li v-html="$t('tips.tax_profile.li_5')"></li>
              </ol>
            </TipBox>
            <TipBox danger no-border>
              <span v-html="$t('tips.tax_profile.danger')"></span>
            </TipBox>
            <div class="tax-profile" v-for="(tp, i) of inventory.taxProfiles" :key="`tp_${i}`">
              <div class="title">
                <span>Configuration #{{ i + 1 }} <template v-if="tp.notes">- {{ tp.notes }}</template></span>
                <button class="btn btn-primary btn-remove" @click="removeTaxProfile(i)"
                  v-if="$store.getters.loggedUserHasModifyPermission">
                  <svgicon src="/icon/close.svg" :height="10" :width="10"></svgicon>
                </button>
              </div>
              <div class="columns">
                <!-- left -->
                <div class="column">
                  <div class="group">
                    <label>{{ $t('inventory.profile.methods') }}</label>
                    <br>
                    <div class="columns">
                      <div class="column">
                        <div class="group offset" v-for="m of supportedMethods.slice(0, 3)" :key="`method_${m.id}`">
                          <input :id="`chk_pro_${i}_${m.id}`" type="checkbox" :value="m.id" v-model="tp.methods">
                          <label :for="`chk_pro_${i}_${m.id}`">{{ $t(m.transKey) }}</label>
                        </div>
                      </div>
                      <div class="column">
                        <div class="group offset" v-for="m of supportedMethods.slice(3, 6)" :key="`method_${m.id}`">
                          <input :id="`chk_pro_${i}_${m.id}`" type="checkbox" :value="m.id" v-model="tp.methods">
                          <label :for="`chk_pro_${i}_${m.id}`">{{ $t(m.transKey) }}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="group" v-if="!tp.allCategories">
                    <label>{{ $t('inventory.profile.categories') }}</label>
                    <AjaxSelectMulti v-model="tp.categories" api="/api/entity/category/select"
                      :query="{ inventory: inventory.id }" :mapFn="c => c.name" :allowEmptySearch="true" />
                  </div>
                  <div class="group">
                    <input type="checkbox" :id="`chk_all_cat_${i}`" v-model="tp.allCategories">
                    <label :for="`chk_all_cat_${i}`">{{ $t('inventory.profile.all_categories') }}</label>
                  </div>
                </div>
                <!-- right -->
                <div class="column">
                  <div class="group">
                    <label>{{ $t('inventory.profile.taxes') }}</label>
                    <div class="field">
                      <AjaxSelect v-model="tp.taxGroup" api="/api/tax_groups"
                        :mapFn="tg => `${tg.uniqueName} (${tg.taxes.map(t => t.name.en).join(',')})`"
                        :allowEmptySearch="true" />
                      <button class="btn btn-secondary remove"
                        v-if="tp.taxGroup.id && $store.getters.loggedUserHasModifyPermission" @click="removeTaxGroup(tp)">
                        <svgicon src="/icon/close.svg" :height="10" :width="10"></svgicon>
                      </button>
                    </div>

                  </div>
                  <div class="group">
                    <label>{{ $t('inventory.profile.description') }}</label>
                    <input type="text" v-model="tp.notes">
                  </div>
                </div>
              </div>
            </div>

            <div style="text-align: center">
              <button class="btn btn-primary add-profile" @click="addTaxProfile"
                v-if="$store.getters.loggedUserHasModifyPermission">{{ $t('inventory.profile.add') }}</button>
            </div>
          </template>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="save btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import AjaxSelect from "@/components/tools/AjaxSelect.vue";
import AjaxSelectMulti from "@/components/tools/AjaxSelectMulti.vue";

export default {
  name: "Inventory",

  components: { AjaxSelect, AjaxSelectMulti },

  data() {
    return {
      supportedLanguages: [
        { id: 'en', transKey: 'translation.en' },
        { id: 'fr', transKey: 'translation.fr' },
      ],
      supportedCurrencies: [
        { id: 'CAD' },
        { id: 'EUR' },
        { id: 'GBP' },
        { id: 'USD' },
      ],
      supportedMethods: [
        { id: 'delivery', transKey: 'deliveryMethod.delivery' },
        { id: 'takeout', transKey: 'deliveryMethod.takeout' },
        { id: 'inPlace', transKey: 'deliveryMethod.inPlace' },
        { id: 'catering', transKey: 'deliveryMethod.catering' },
        { id: 'hotel', transKey: 'deliveryMethod.hotel' },
      ],
      inventory: {
        id: null,
        currency: "CAD",
        name: null,
        languages: [],
        taxesIncluded: false,
        showItemsWhenZero: false,
        orderRequirementsActivated: false,
        brandingActivated: false,
        allowCrossBrandOrdering: false,
        taxProfiles: []
      },
      augment: 'backend',
      selectedBranchInventory: null,
      company: {
        inventory: null,
        originalInventory: null,
        archivedInventories: []
      }
    }
  },

  async mounted() {
    this.$store.state.pageTitleTranslationKey = "inventory.configuration";
    this.$store.state.pageIcon = "/icon/menu.svg";
    if (this.isCompanyContext) {
      await this.loadCompany();
    }
  },

  computed: {
    contextInventory() {
      return this.$store.state.contextInventory ? this.$store.state.contextInventory.id : null
    },
    isCompanyContext() {
      return !!this.$route.query.company;
    },
    canEditInventory() {
      return (!this.isBranchInventory && !this.selectedBranchInventory) || !this.isCompanyContext;
    },
    isBranchInventory() {
      return !!this.branchInventories.find(i => i.id === this.company.inventory);
    },
    isArchiveInventory() {
      return !!this.company.archivedInventories.find(i => i.id === this.company.inventory);
    },
    branchInventories() {
      return this.$store.state.contextBranch ? this.$store.state.contextBranch.inventories : [];
    }
  },

  watch: {
    contextInventory: {
      immediate: true,
      async handler(inventoryId) {
        if (inventoryId) {
          this.$store.commit('showSpinner', true);
          let response = await this.getInventory({ id: inventoryId });
          this.$store.commit("showSpinner", false);
          this.setInventory(response);

          if (!this.$store.getters.loggedUserHasModifyPermission) {
            window.lockFields();
          }
        }
      }
    },
    selectedBranchInventory(newValue, oldValue) {
      if (oldValue == null) {
        return;
      }
      if (!this.selectedBranchInventory && this.company.originalInventory) {
        this.company.inventory = this.company.originalInventory;
      } else {
        this.company.inventory = null;
      }
    }
  },

  methods: {
    ...mapActions(["getInventory", "saveInventory", "getCompany", "saveCompany"]),

    setInventory(response) {
      for (let key of Object.keys(this.inventory)) {
        this.inventory[key] = response[key]
      }
      for (let tp of this.inventory.taxProfiles) {
        if (!tp.taxGroup) {
          tp.taxGroup = { id: null }
        }
      }
    },

    async loadCompany() {
      this.$store.commit("showSpinner", true);
      let response = await this.getCompany({
        branchId: this.$route.query.branch,
        companyId: this.$route.query.company,
        augment: this.augment
      });
      if (response) {
        this.company.inventory = response.inventory ? response.inventory.id : null;
        this.company.archivedInventories = response.archivedInventories;
        this.selectedBranchInventory = this.isBranchInventory;
        if (!this.isBranchInventory && !this.isArchiveInventory) {
          this.company.originalInventory = this.company.inventory;
        }
      } else {
        toast(this.$t("error.no_server"), "error");
      }
      this.$store.commit("showSpinner", false);
    },

    addTaxProfile() {
      this.inventory.taxProfiles.push({
        id: null,
        taxGroup: { id: null },
        categories: [],
        allCategories: false,
        methods: [],
        notes: null,
      })
    },

    removeTaxProfile(index) {
      this.inventory.taxProfiles.splice(index, 1)
    },

    removeTaxGroup(profile) {
      profile.taxGroup = { id: null }
    },

    async save() {
      if (this.isCompanyContext && !this.company.inventory) {
        toast(this.$t("inventory.error.select"), "warning");
        return;
      }

      /* Validation */
      let errors = [];
      let mapByMethod = this.supportedMethods
        .map(e => e.id)
        .reduce((obj, e) => Object.assign(obj, { [e]: [] }), {});

      for (let p of this.inventory.taxProfiles) {
        for (let c of p.categories) {
          for (let m of p.methods) {
            if (mapByMethod[m].includes(c.id)) {
              errors.push(
                this.$t('inventory.profile_category_duplicated_error')
                  .replace('%CATEGORY%', this.$to(c.name))
                  .replace('%METHOD%', this.$t(`deliveryMethod.${m}`))
              )
            } else {
              mapByMethod[m].push(c.id)
            }
          }
        }
      }
      if (errors.length > 0) {
        toast([`<b>${this.$t('error.title')}</b>`].concat(errors).join('<br>'), 'error');
        return;
      }

      /* Remapping */
      let copy = JSON.parse(JSON.stringify(this.inventory));
      for (let p of copy.taxProfiles) {
        p.categories = p.categories.map(c => c.id);
        p.taxGroup = p.taxGroup.id;
        if (p.allCategories) {
          p.categories = []
        }
      }

      this.$store.commit("showSpinner", true);
      let inventoryResponse = true;
      let companyResponse = true;
      if (this.canEditInventory) {
        inventoryResponse = await this.saveInventory({ id: this.inventory.id, data: copy });
      }
      if (this.isCompanyContext) {
        companyResponse = await this.saveCompany({
          branchId: this.$route.query.branch, companyId: this.$route.query.company, augment: this.augment, data: {
            inventory: this.company.inventory
          }
        });
      }
      this.$store.commit("showSpinner", false);
      if (inventoryResponse && companyResponse) {
        if (this.canEditInventory) {
          this.setInventory(inventoryResponse);
          let foundBranchInventory = this.branchInventories.find(i => i.id === this.inventory.id);
          if (foundBranchInventory) {
            let index = this.branchInventories.indexOf(foundBranchInventory);
            this.branchInventories[index] = inventoryResponse;
          }

          this.$store.state.contextInventory = inventoryResponse;
        }
        if (this.isCompanyContext) {
          this.$store.state.contextCompany = companyResponse;
          this.$store.state.contextInventory = companyResponse.inventory;
        }
        toast(this.$t("inventory.success"), "success");
      } else {
        toast(this.$t("inventory.error.save"), "error");
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#inventoryEdit {
  display: flex;
  flex-direction: column;
}

.offset {
  margin-left: 15px;
}

.tax-profile {
  padding-top: 20px;

  &:not(:last-child) {
    border-bottom: 1px dashed #d9d9d9;
  }

  .title {
    font-weight: bold;
    font-size: 18px;
    padding-bottom: 15px;
    border-bottom: 1px solid #d9d9d9;
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .btn {
    fill: white;
    padding: 10px;
  }
}

.add-profile {
  margin-top: 20px
}

.field {
  display: flex;
  width: 100%;

  .ajax-select {
    flex-grow: 1;
  }

  .remove {
    fill: var(--primary-color);
    border: 1px solid var(--primary-color);
    padding: 12px;
    margin-left: 10px;
  }
}</style>
